import { useNavigate } from "react-router-dom";
import { MaintenanceTask } from "@/lib/types";
import { Mixpanel } from "@/lib/mixpanel";
import { useMemo } from "react";
import Boat from "@/components/icons/boat";
import { useTranslation } from "react-i18next";
import Card from "@/components/custom/card";
import { Warning } from "@/components/icons";
import { useHasPermission } from "@/hooks/use-permission";
import { PERMISSIONS } from "@/lib/constants/permissions";

const TaskCard = ({
  task,
  redirectPath,
}: {
  task: MaintenanceTask;
  redirectPath?: string;
}) => {
  const navigate = useNavigate();
  const { t } = useTranslation();
  const canReadMaintenance = useHasPermission(PERMISSIONS.Maintenance.Read);

  const overdue = useMemo(() => {
    return task.taskDetail.timeToDueScore && task.taskDetail.timeToDueScore < 0;
  }, [task.taskDetail.timeToDueScore]);

  return (
    <Card
      data-testid={`task-card-${task.id}`}
      onClick={() => {
        if (!canReadMaintenance) return;

        navigate(`/task/${task.id}`, {
          state: {
            redirectPath,
          },
        });
        Mixpanel.track("Navigate to Task Details");
      }}
    >
      <div className="flex flex-row items-center justify-center gap-2">
        <span className="flex items-center justify-center text-white/50">
          <Boat color="#ffffff50" size={20} />
        </span>

        <div className="flex flex-col items-start justify-start gap-2">
          <span className="line-clamp-1 text-ellipsis text-sm font-medium leading-[14px] text-white">
            {task.name}
          </span>
          <div className="flex flex-wrap items-center gap-1">
            <span className="rounded-full bg-white/10 px-2 py-0.5 text-[10px] font-medium text-white/80">
              {t("label.task")}
            </span>
            {task.taskDetail.assignees?.length > 0 && (
              <span className="line-clamp-1 text-ellipsis text-[10px] font-medium text-[#7ECEE8]">
                {task.taskDetail.assignees.map((a) => a.fullName).join(", ")}
              </span>
            )}
          </div>
        </div>
      </div>

      <div className="flex flex-row items-center gap-4">
        <div className="flex flex-col items-end gap-1">
          <div className="flex items-center gap-[6px]">
            <span className="whitespace-nowrap text-xs font-medium leading-4 text-[#F6B11A]">
              {overdue ? t("label.due_date_overdue") : ""}
            </span>

            {overdue && <Warning size={16} color="#F6B11A" />}
          </div>
        </div>
      </div>
    </Card>
  );
};

export default TaskCard;
